import { plural, t } from '@lingui/macro';

export enum ServiceType {
  OVERNIGHT_TRAVELING = 'overnight-traveling',
  OVERNIGHT_BOARDING = 'overnight-boarding',
  DOG_WALKING = 'dog-walking',
  DROP_IN = 'drop-in',
  DOGGY_DAY_CARE = 'doggy-day-care',
  ON_DEMAND_DOG_WALKING = 'on-demand-dog-walking',
  GROOMING = 'on-demand-grooming',
  ON_DEMAND_DROP_IN = 'on-demand-drop-in',
  DOG_TRAINING = 'dog-training',
}

export const DAYTIME_SERVICES = [
  ServiceType.DROP_IN,
  ServiceType.DOGGY_DAY_CARE,
  ServiceType.DOG_WALKING,
];

export const SERVICE_TYPE_CHOICES: Record<string, ServiceType> = {
  boarding: ServiceType.OVERNIGHT_BOARDING,
  traveling: ServiceType.OVERNIGHT_TRAVELING,
  dropIn: ServiceType.DROP_IN,
  dayCare: ServiceType.DOGGY_DAY_CARE,
  walking: ServiceType.DOG_WALKING,
  training: ServiceType.DOG_TRAINING,
  onDemandWalking: ServiceType.ON_DEMAND_DOG_WALKING,
  grooming: ServiceType.GROOMING,
  assignmentDropIn: ServiceType.ON_DEMAND_DROP_IN,
};

export const GUARANTEE_SERVICE_TYPE_CHOICES = [
  SERVICE_TYPE_CHOICES.boarding,
  SERVICE_TYPE_CHOICES.traveling,
  SERVICE_TYPE_CHOICES.dropIn,
  SERVICE_TYPE_CHOICES.dayCare,
  SERVICE_TYPE_CHOICES.walking,
];

// model after SERVICE_TYPE_PRICE_UNITS or ALTERNATIVE_RATE_UNIT
export const ALTERNATIVE_RATE_UNITS = (): Record<string, (count: number) => string> => ({
  night: (count: number): string =>
    plural(count, {
      one: 'night',
      other: 'nights',
    }),
  day: (count: number): string =>
    plural(count, {
      one: 'day',
      other: 'days',
    }),
  visit: (count: number): string =>
    plural(count, {
      one: 'visit',
      other: 'visits',
    }),
  walk: (count: number): string =>
    plural(count, {
      one: 'walk',
      other: 'walks',
    }),
  groom: (count: number): string =>
    plural(count, {
      one: 'groom',
      other: 'grooms',
    }),
});

// model after UNIT_CHOICES which is used in AddOnType.unit
export const ADD_ON_TYPE_UNITS = (): Record<string, (count: number) => string> => ({
  night: (count: number): string =>
    plural(count, {
      one: 'night',
      other: 'nights',
    }),
  bath: (count: number): string =>
    plural(count, {
      one: 'groom',
      other: 'grooms',
    }),
  walk: (count: number): string =>
    plural(count, {
      one: 'walk',
      other: 'walks',
    }),
  round_trip: (count: number): string =>
    plural(count, {
      one: 'round trip',
      other: 'round trips',
    }),
  session: (count: number): string =>
    plural(count, {
      one: 'session',
      other: 'sessions',
    }),
});

// all possible unit values returned by service.add_on.unit. See services.models.ServiceAddOnMixin#get_rate_unit in backend
export const SERVICE_ADD_ON_UNITS = () => ({ ...ALTERNATIVE_RATE_UNITS(), ...ADD_ON_TYPE_UNITS() });

// SERVICE_TYPE_ALTERNATIVE_RATE_UNIT, model after SERVICE_TYPE_PRICE_UNITS
// @ts-expect-error: Training is missing from this set
export const SERVICE_TYPE_PRICE_UNITS = (): Record<ServiceType, (count: number) => string> => ({
  [SERVICE_TYPE_CHOICES.boarding]: ALTERNATIVE_RATE_UNITS().night,
  [SERVICE_TYPE_CHOICES.traveling]: ALTERNATIVE_RATE_UNITS().night,
  [SERVICE_TYPE_CHOICES.dayCare]: ALTERNATIVE_RATE_UNITS().day,
  [SERVICE_TYPE_CHOICES.dropIn]: ALTERNATIVE_RATE_UNITS().visit,
  [SERVICE_TYPE_CHOICES.walking]: ALTERNATIVE_RATE_UNITS().walk,
  [SERVICE_TYPE_CHOICES.onDemandWalking]: ALTERNATIVE_RATE_UNITS().walk,
  [SERVICE_TYPE_CHOICES.grooming]: ALTERNATIVE_RATE_UNITS().groom,
  [SERVICE_TYPE_CHOICES.assignmentDropIn]: ALTERNATIVE_RATE_UNITS().visit,
});

export const SERVICE_TYPE_DESCRIPTION = (): Partial<Record<ServiceType, string>> => ({
  [ServiceType.OVERNIGHT_BOARDING]: t`Your pets stay overnight in your sitter’s home.`,
  [ServiceType.OVERNIGHT_TRAVELING]: t`Your sitter takes care of your pets and your house.`,
  [ServiceType.DOGGY_DAY_CARE]: t`Your dog spends the day with a trusted local day care provider.`,
  [ServiceType.DROP_IN]: t`Your sitter drops by your home to play, feed, and give your pet potty breaks or clean the litter box.`,
  [ServiceType.DOG_WALKING]: t`Your dog gets a walk around your neighborhood.`,
});

export const GET_SERVICE_TYPE_FROM_SLUG = (slug: string): ServiceType | undefined => {
  const found = Object.entries(SERVICE_TYPE_CHOICES).find(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([serviceTypeKey, serviceTypeValue]) => serviceTypeValue === slug
  );
  if (found) {
    return found[1];
  }
  return undefined;
};

export const DEFAULT_PRINTABLE_BOOKING_FEE_PERCENTAGE = '11%';
export const DEFAULT_PRINTABLE_BOOKING_FEE_CAP = '$50';
